import { Box, Heading, Input, InputGroup, InputLeftElement, Stack, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { useFormContext } from 'react-hook-form';
import { HiOutlineSearch } from 'react-icons/hi';
import usePlacesAutocomplete, { getGeocode, getLatLng, Suggestion } from 'use-places-autocomplete';

import { AddressForm } from './AddressForm';

type Props = {
  name?: string;
};

// Pickup address as a full, always-visible form with an address search on top:
// selecting a search result autofills the AddressForm fields below, but the
// fields stay editable for manual corrections.
export const PickupAddressForm = ({ name = 'cargo_origin' }: Props) => {
  const { setValue } = useFormContext();

  const {
    ready,
    suggestions: { status, data },
    value: query,
    setValue: setQuery,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: { componentRestrictions: { country: ['US'] } },
  });

  const handleSelect = (prediction: Suggestion) => async () => {
    setQuery(prediction.description, false);
    clearSuggestions();

    const result = (await getGeocode({ address: prediction.description }))?.[0];
    if (!result) return;

    const { lat: latitude, lng: longitude } = getLatLng(result);

    const getAddressComponent = (type: string) =>
      result.address_components?.find(({ types }) => types?.includes(type))?.short_name ?? '';

    const line_one = [getAddressComponent('street_number'), getAddressComponent('route')].filter(Boolean).join(' ');

    setValue(
      name,
      {
        recipient: prediction.structured_formatting?.main_text || '',
        line_one,
        line_two: getAddressComponent('subpremise'),
        country: getAddressComponent('country'),
        state: getAddressComponent('administrative_area_level_1'),
        city: getAddressComponent('locality'),
        zip_code: getAddressComponent('postal_code'),
        latitude,
        longitude,
        place_id: result.place_id,
      },
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const showSuggestions = status === 'OK' && data.length > 0;

  return (
    <Stack spacing="2">
      <Heading fontSize="sm" textTransform="uppercase">
        Address
      </Heading>
      <Box position="relative">
        <InputGroup>
          <InputLeftElement pointerEvents="none" color="text.placeholder">
            <HiOutlineSearch />
          </InputLeftElement>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            isDisabled={!ready}
            placeholder="Search address, city, or zip"
            variant="outline"
            autoComplete="off"
          />
        </InputGroup>
        <Show if={showSuggestions}>
          <Stack
            position="absolute"
            zIndex="dropdown"
            mt="1"
            w="full"
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.light"
            borderRadius="md"
            boxShadow="lg"
            spacing="0"
            maxH="240px"
            overflowY="auto"
          >
            {data.map((prediction) => (
              <Box
                key={prediction.place_id}
                px="3"
                py="2"
                cursor="pointer"
                _hover={{ bg: 'bg.default' }}
                onClick={handleSelect(prediction)}
              >
                <Text fontSize="sm">{prediction.description}</Text>
              </Box>
            ))}
          </Stack>
        </Show>
      </Box>
      <AddressForm name={name} columns={2} />
    </Stack>
  );
};
